// A household card that edits in place: the pencil turns the card over to the mounted editor and
// becomes Save, with Discard beside it. The editor is always mounted behind the saved facts, so
// its draft, catalog reads, captured token and permission checks are the existing ones; this card
// only relocates the controls it publishes and animates the reveal.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from "react"
import { IconCheck, IconEye, IconLoader2, IconPencil, IconX } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { InlineCardEditHost, type InlineEditorControls } from "./inline-card-edit-host"
import "./record-action.css"
import "./household-details.css"
import "./inline-card-edit.css"

const firstField = 'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [data-slot="select-trigger"]:not([disabled]), button:not([disabled])'

function CardAction({ label, icon, onClick, disabled, primary, unavailableReason, ref }: {
  label: string; icon: ReactNode; onClick: () => void; disabled?: boolean; primary?: boolean; unavailableReason?: string | null; ref?: Ref<HTMLButtonElement>
}) {
  const [helpOpen, setHelpOpen] = useState(false)
  const [helpPinned, setHelpPinned] = useState(false)
  const descriptionId = useId()
  return <>
    {unavailableReason && <span id={descriptionId} className="sr-only">{unavailableReason}</span>}
    <Tooltip open={helpOpen || (!!unavailableReason && helpPinned)} onOpenChange={setHelpOpen}><TooltipTrigger asChild>
      <Button ref={ref} variant="outline" size="icon" className="record-action" data-primary={primary || undefined}
        aria-label={label} aria-describedby={unavailableReason ? descriptionId : undefined}
        aria-disabled={unavailableReason ? true : undefined} disabled={disabled}
        // Keyboard focus pins the explanation: focusing can scroll the button into view, and the
        // tooltip closes on ancestor scroll, so unpinned help could vanish as it appears.
        onFocus={event => { if (unavailableReason && event.currentTarget.matches(":focus-visible")) setHelpPinned(true) }}
        onBlur={() => setHelpPinned(false)}
        onClick={() => {
          // aria-disabled keeps the explanation reachable by keyboard and touch. It must
          // never call the editor handler; native disabled remains for pending Save/Discard.
          if (unavailableReason) { setHelpPinned(true); return }
          onClick()
        }}>{icon}</Button>
    </TooltipTrigger><TooltipContent sideOffset={5} className="max-w-[min(22rem,calc(100vw-2rem))]"
      onEscapeKeyDown={() => setHelpPinned(false)} onPointerDownOutside={() => setHelpPinned(false)}>
      {unavailableReason ?? label}
    </TooltipContent></Tooltip>
  </>
}

export function InlineEditCard({ kind, title, noun, description, facts, editor, hint, className, editDisabledReason, editReadOnly, onDetails, detailsLabel }: {
  /** Stable identity of the hosted editor, e.g. "intake". */
  kind: string
  title: string
  /** Noun used in the action names, e.g. "contact & intake details". */
  noun: string
  description?: ReactNode
  facts: ReactNode
  editor: ReactNode
  hint?: ReactNode
  className?: string
  editDisabledReason?: string | null
  /** Saved facts this card shows that its editor does not own. The form hides nothing: these
   * stay visible while editing, as read-only rows that say where they are maintained. */
  editReadOnly?: { rows: [string, ReactNode][]; note: ReactNode }
  /** Opens the card's detail panel when the card summarizes more than it displays. */
  onDetails?: () => void
  detailsLabel?: string
}) {
  const [controls, setControls] = useState<InlineEditorControls | null>(null)
  const publish = useCallback((next: InlineEditorControls | null) => { setControls(next) }, [])
  const editing = controls?.isEditing ?? false
  const saving = controls?.isSaving ?? false
  const pencil = useRef<HTMLButtonElement | null>(null)
  const heading = useRef<HTMLHeadingElement | null>(null)
  const front = useRef<HTMLDivElement | null>(null)
  const back = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number | null>(null)
  // The turned-away face keeps its box so the card can measure it, but it is hidden once the
  // flip has settled: find-in-page and text selection only ever reach the face in use.
  const [turning, setTurning] = useState(false)
  const mounted = useRef(false)
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    setTurning(true)
    const timer = setTimeout(() => setTurning(false), 600)
    return () => clearTimeout(timer)
  }, [editing])
  const faceHidden = (face: "front" | "back") => (turning || editing === (face === "back") ? undefined : true)

  // Both faces are out of flow so the closed card keeps its compact height; the visible one
  // supplies the card's height, including later content changes from a refreshed read.
  useLayoutEffect(() => {
    const face = editing ? back.current : front.current
    if (!face) return
    const measure = () => setHeight(face.getBoundingClientRect().height)
    measure()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(face)
    return () => observer.disconnect()
  }, [editing])

  const wasEditing = useRef(editing)
  useEffect(() => {
    const started = editing && !wasEditing.current, ended = !editing && wasEditing.current
    wasEditing.current = editing
    if (!started && !ended) return
    let frame = 0
    // The control that was clicked is replaced by its counterpart, so focus has to be placed
    // deliberately: into the revealed form, and back on the pencil when the card closes.
    // A face that was just revealed can still be unfocusable for a frame or two, so keep
    // trying briefly instead of leaving focus on a control that no longer exists.
    const place = (attempts: number) => {
      // Even after permission loss, the disabled pencil remains a stable help/focus target.
      const target = started ? back.current?.querySelector<HTMLElement>(firstField) : pencil.current ?? heading.current
      if (!target?.isConnected) return
      target.focus({ preventScroll: true })
      if (document.activeElement === target || attempts <= 0) return
      frame = requestAnimationFrame(() => place(attempts - 1))
    }
    frame = requestAnimationFrame(() => place(10))
    return () => cancelAnimationFrame(frame)
  }, [editing])

  return <Card className={`household-surface inline-edit-card${className ? ` ${className}` : ""}`} data-inline-card={kind} data-editing={editing || undefined}>
    <CardHeader className="household-card-header">
      <div><CardTitle><h3 ref={heading} tabIndex={-1}>{title}</h3></CardTitle>{description ? <CardDescription>{description}</CardDescription> : null}</div>
      <TooltipProvider><div className="record-actions inline-edit-actions" role="group" aria-label={`${title} actions`}>
        {editing ? <>
          <CardAction label={`Discard changes to ${noun}`} disabled={saving} onClick={() => controls?.cancel()}
            icon={<IconX className="size-4" aria-hidden="true" />} />
          <CardAction label={`Save ${noun}`} primary disabled={saving || (controls?.saveDisabled ?? true)} onClick={() => controls?.save()}
            icon={saving ? <IconLoader2 className="size-4 animate-spin" aria-hidden="true" /> : <IconCheck className="size-4" aria-hidden="true" />} />
        </> : <>
          {onDetails ? <CardAction label={detailsLabel ?? `View ${noun}`} onClick={onDetails}
            icon={<IconEye className="size-4" aria-hidden="true" />} /> : null}
          <CardAction ref={pencil} label={`Edit ${noun}`} onClick={() => { if (controls?.canEdit && !editDisabledReason) controls.edit() }}
            unavailableReason={editDisabledReason ?? (!controls ? "Checking editing permissions…" : !controls.canEdit ? "Your role or this application's state does not allow editing." : null)}
            icon={<IconPencil className="size-4" aria-hidden="true" />} />
        </>}
      </div></TooltipProvider>
    </CardHeader>
    <CardContent>
      <div className="inline-edit-flip" data-flipped={editing || undefined} style={height === null ? undefined : { height }}>
        <div className="inline-edit-face inline-edit-front" ref={front} inert={editing} data-face-hidden={faceHidden("front")}>
          {facts}
          {hint ? <p className="household-note inline-edit-hint">{hint}</p> : null}
        </div>
        <div className="inline-edit-face inline-edit-back" ref={back} inert={!editing} data-face-hidden={faceHidden("back")}>
          <InlineCardEditHost.Provider value={publish}>{editor}</InlineCardEditHost.Provider>
          {editReadOnly && editReadOnly.rows.length > 0 ? (
            <div className="inline-edit-readonly">
              <p className="inline-edit-readonly-note">{editReadOnly.note}</p>
              <dl className="household-facts">{editReadOnly.rows.map(([label, value]) => (
                <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
              ))}</dl>
            </div>
          ) : null}
          {editing ? <p role="status" className="household-note inline-edit-hint">Changes apply only when saved.</p> : null}
        </div>
      </div>
    </CardContent>
  </Card>
}
